import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { InvoiceCard } from '../components/InvoiceCard';
import { DemoBanner } from '../components/DemoBanner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useWeb3 } from '../contexts/Web3Context';
import { Plus, Loader2, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { getInvoiceNFTContract } from '../utils/contracts';
import { ethers } from 'ethers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const MSMEDashboard = () => {
  const navigate = useNavigate();
  const { account, signer, isConnected } = useWeb3();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showMintForm, setShowMintForm] = useState(false);
  const [minting, setMinting] = useState(false);
  const [contractAddresses, setContractAddresses] = useState(null);

  const [formData, setFormData] = useState({
    amount: '',
    dueDate: '',
    debtorAddress: '',
    riskScore: 5,
    companyName: '',
    invoiceNumber: '',
    description: '',
  });

  useEffect(() => {
    if (isConnected && account) {
      fetchWalletInvoices();
      fetchContractAddresses();
    }
  }, [isConnected, account]);

  const fetchContractAddresses = async () => {
    try {
      const response = await axios.get(`${API}/config/contracts`);
      setContractAddresses(response.data);
    } catch (error) {
      console.error('Error fetching contract addresses:', error);
    }
  };

  const fetchWalletInvoices = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/invoices/wallet/${account}`);
      setInvoices(response.data);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error('Failed to fetch invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleMintInvoice = async (e) => {
    e.preventDefault();

    if (!isConnected) {
      toast.error('Please connect your wallet first');
      return;
    }

    if (!contractAddresses?.invoice_nft) {
      toast.error('Contract addresses not configured. Please deploy contracts first.');
      return;
    }

    try {
      setMinting(true);

      const invoiceNFT = getInvoiceNFTContract(signer, contractAddresses.invoice_nft);
      const amountInWei = ethers.parseEther(formData.amount.toString());
      const dueDateTimestamp = Math.floor(new Date(formData.dueDate).getTime() / 1000);
      const ipfsHash = `mock-ipfs-${Date.now()}`;

      toast.info('Waiting for transaction confirmation...');

      const tx = await invoiceNFT.mintInvoice(
        amountInWei,
        dueDateTimestamp,
        formData.debtorAddress,
        formData.riskScore,
        ipfsHash
      );

      toast.info('Transaction submitted. Waiting for confirmation...');
      const receipt = await tx.wait();

      const tokenId = receipt.logs[0]?.topics[1] ? parseInt(receipt.logs[0].topics[1], 16) : null;

      const invoiceData = {
        token_id: tokenId,
        amount: parseFloat(formData.amount),
        due_date: formData.dueDate,
        debtor_address: formData.debtorAddress.toLowerCase(),
        msme_address: account.toLowerCase(),
        risk_score: formData.riskScore,
        ipfs_hash: ipfsHash,
        company_name: formData.companyName,
        invoice_number: formData.invoiceNumber,
        description: formData.description,
        status: 'LISTED',
      };

      await axios.post(`${API}/invoices`, invoiceData);

      await axios.post(`${API}/transactions`, {
        invoice_id: tokenId?.toString() || 'pending',
        transaction_hash: receipt.hash,
        transaction_type: 'MINT',
        from_address: account.toLowerCase(),
        amount: parseFloat(formData.amount),
      });

      toast.success('Invoice minted successfully!');
      setShowMintForm(false);
      setFormData({
        amount: '',
        dueDate: '',
        debtorAddress: '',
        riskScore: 5,
        companyName: '',
        invoiceNumber: '',
        description: '',
      });
      fetchWalletInvoices();
    } catch (error) {
      console.error('Error minting invoice:', error);
      toast.error(error.message || 'Failed to mint invoice');
    } finally {
      setMinting(false);
    }
  };

  const handleViewDetails = (invoice) => {
    navigate(`/invoice/${invoice.token_id || invoice.id}`);
  };

  if (!isConnected) {
    return (
      <div className="App min-h-screen">
        <Navbar />
        <div data-testid="connect-wallet-prompt" className="container mx-auto px-6 py-24">
          <div className="max-w-md mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4 text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Connect Your Wallet
            </h2>
            <p className="text-slate-600 mb-8">
              Please connect your MetaMask wallet to access the MSME dashboard.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="App min-h-screen">
      <Navbar />
      
      <div className="container mx-auto px-6 py-12">
        <DemoBanner />
        
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 data-testid="msme-dashboard-title" className="text-4xl font-bold mb-2 text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
              MSME Dashboard
            </h1>
            <p className="text-slate-600">Mint and manage your invoice NFTs</p>
          </div>
          <Button
            data-testid="mint-invoice-button"
            onClick={() => setShowMintForm(!showMintForm)}
            className="gradient-bg text-white hover:opacity-90 font-semibold button-scale shadow-lg shadow-indigo-500/30"
          >
            <Plus className="mr-2 h-4 w-4" />
            Mint Invoice
          </Button>
        </div>

        {showMintForm && (
          <div data-testid="mint-form" className="professional-card p-8 mb-8">
            <h2 className="text-2xl font-bold mb-6 text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Mint New Invoice
            </h2>
            <form onSubmit={handleMintInvoice} className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="companyName" className="text-sm font-semibold text-slate-700 mb-2 block">
                  Company Name
                </Label>
                <Input
                  id="companyName"
                  data-testid="company-name-input"
                  value={formData.companyName}
                  onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                  required
                  placeholder="Acme Corp"
                  className="border-slate-300 focus:border-indigo-600 focus:ring-indigo-600"
                />
              </div>

              <div>
                <Label htmlFor="invoiceNumber" className="text-sm font-semibold text-slate-700 mb-2 block">
                  Invoice Number
                </Label>
                <Input
                  id="invoiceNumber"
                  data-testid="invoice-number-input"
                  value={formData.invoiceNumber}
                  onChange={(e) => setFormData({ ...formData, invoiceNumber: e.target.value })}
                  required
                  placeholder="INV-2025-001"
                  className="border-slate-300 focus:border-indigo-600 focus:ring-indigo-600"
                />
              </div>

              <div>
                <Label htmlFor="amount" className="text-sm font-semibold text-slate-700 mb-2 block">
                  Amount (ETH)
                </Label>
                <Input
                  id="amount"
                  data-testid="amount-input"
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  required
                  placeholder="1.5"
                  className="border-slate-300 focus:border-indigo-600 focus:ring-indigo-600"
                />
              </div>

              <div>
                <Label htmlFor="dueDate" className="text-sm font-semibold text-slate-700 mb-2 block">
                  Due Date
                </Label>
                <Input
                  id="dueDate"
                  data-testid="due-date-input"
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                  required
                  min={new Date().toISOString().split('T')[0]}
                  className="border-slate-300 focus:border-indigo-600 focus:ring-indigo-600"
                />
              </div>

              <div>
                <Label htmlFor="debtorAddress" className="text-sm font-semibold text-slate-700 mb-2 block">
                  Debtor Wallet Address
                </Label>
                <Input
                  id="debtorAddress"
                  data-testid="debtor-address-input"
                  value={formData.debtorAddress}
                  onChange={(e) => setFormData({ ...formData, debtorAddress: e.target.value })}
                  required
                  placeholder="0x..."
                  className="border-slate-300 focus:border-indigo-600 focus:ring-indigo-600"
                />
              </div>

              <div>
                <Label htmlFor="riskScore" className="text-sm font-semibold text-slate-700 mb-2 block">
                  Risk Score (1-10)
                </Label>
                <Input
                  id="riskScore"
                  data-testid="risk-score-input"
                  type="number"
                  min="1"
                  max="10"
                  value={formData.riskScore}
                  onChange={(e) => setFormData({ ...formData, riskScore: parseInt(e.target.value) })}
                  required
                  className="border-slate-300 focus:border-indigo-600 focus:ring-indigo-600"
                />
              </div>

              <div className="md:col-span-2">
                <Label htmlFor="description" className="text-sm font-semibold text-slate-700 mb-2 block">
                  Description
                </Label>
                <Input
                  id="description"
                  data-testid="description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Invoice description"
                  className="border-slate-300 focus:border-indigo-600 focus:ring-indigo-600"
                />
              </div>

              <div className="md:col-span-2 flex gap-4">
                <Button
                  data-testid="submit-mint-button"
                  type="submit"
                  disabled={minting}
                  className="gradient-bg text-white hover:opacity-90 font-semibold shadow-lg shadow-indigo-500/30"
                >
                  {minting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Minting...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="mr-2 h-4 w-4" />
                      Mint Invoice NFT
                    </>
                  )}
                </Button>
                <Button
                  data-testid="cancel-mint-button"
                  type="button"
                  variant="outline"
                  onClick={() => setShowMintForm(false)}
                  className="border-slate-300 hover:border-red-500 hover:text-red-600"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        )}

        <div data-testid="invoices-section">
          <h2 className="text-2xl font-bold mb-6 text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Your Invoices
          </h2>
          {loading ? (
            <div data-testid="loading-spinner" className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
            </div>
          ) : invoices.length === 0 ? (
            <div data-testid="no-invoices" className="text-center py-12 text-slate-600">
              <p>No invoices found. Mint your first invoice to get started!</p>
            </div>
          ) : (
            <div data-testid="invoices-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {invoices.map((invoice) => (
                <InvoiceCard
                  key={invoice.id}
                  invoice={invoice}
                  onViewDetails={handleViewDetails}
                  userType="msme"
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};